import sanitizeHtml from "sanitize-html";

const ELLIPSIS = "\u2026";

function escapeHtml(text) {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

/**
 * Split sanitized HTML (text + <em> only) into flat segments, with
 * whitespace runs collapsed so the character budget reflects rendered
 * width rather than the source's padding.
 */
function toSegments(html) {
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
    const segments = [];

    doc.body.childNodes.forEach(node => {
        const text = (node.nodeType === Node.TEXT_NODE ? node.nodeValue : node.textContent)
              .replace(/\s+/g, " ");
        if (text) {
            segments.push({text, em: node.nodeType !== Node.TEXT_NODE});
        }
    });

    return segments;
}

/**
 * Snap an offset to a nearby word boundary, giving up if the nearest one
 * is too far (ASCII art has very long unbroken tokens).
 */
function snap(text, offset, direction, limit = 20) {
    for (let i = 0; i < limit; i++) {
        const probe = offset + i * direction;
        if (probe <= 0 || probe >= text.length) return offset;
        if (/\s/.test(text[probe])) return probe + (direction > 0 ? 1 : 0);
    }
    return offset;
}

function windowAroundMatch(html, maxChars) {
    const segments = toSegments(html);
    const plain = segments.map(s => s.text).join("");

    if (plain.length <= maxChars) {
        return segments.map(
            s => s.em ? `<em>${escapeHtml(s.text)}</em>` : escapeHtml(s.text)
        ).join("");
    }

    // Locate the first highlight in plain-text coordinates.
    let cursor = 0;
    let matchStart = null;
    let matchEnd = null;

    for (const seg of segments) {
        if (seg.em && matchStart === null) {
            matchStart = cursor;
            matchEnd = cursor + seg.text.length;
        }
        cursor += seg.text.length;
    }

    let start;
    if (matchStart === null) {
        start = 0;                                   // no highlight: show the head
    } else {
        const center = (matchStart + matchEnd) / 2;
        start = Math.round(center - maxChars / 2);
    }

    start = Math.max(0, Math.min(start, plain.length - maxChars));
    let end = start + maxChars;

    if (start > 0) start = snap(plain, start, 1);
    if (end < plain.length) end = snap(plain, end, -1);

    // Rebuild only the segments overlapping the window.
    let out = start > 0 ? ELLIPSIS + " " : "";
    cursor = 0;

    for (const seg of segments) {
        const segStart = cursor;
        const segEnd = cursor + seg.text.length;
        cursor = segEnd;

        if (segEnd <= start || segStart >= end) continue;

        const slice = seg.text.slice(
            Math.max(0, start - segStart),
            Math.min(seg.text.length, end - segStart)
        );
        out += seg.em ? `<em>${escapeHtml(slice)}</em>` : escapeHtml(slice);
    }

    if (end < plain.length) out += " " + ELLIPSIS;
    return out;
}


/**
 * Component to display Search result highlight information from
 * Solar, which includes HTML, in a safe way. This function only allows
 * the <em> tag, and filters out all other tags.
 *
 * @param props.html Raw HTML display to sanitize
 * @param props.style Style dictionary applies to the div
 * @param props.className Class Name(s) applied to the div
 * @return JSX component
 */
export function SanitizedSolrHighlightHtml(props) {
    const cleanHtml = sanitizeHtml(props.html, {
        allowedTags: ["em"],
        allowedAttributes: {},
        allowedIframeHostnames: [],
    });

    const finalHtml = props.maxChars
          ? windowAroundMatch(cleanHtml, props.maxChars)
          : cleanHtml;

    return (
        <div
          style={props.style}
          className={props.className}
          dangerouslySetInnerHTML={{__html: finalHtml}}
        />
    );
}
